import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiChevronDown } from "react-icons/fi";

/**
 * Accessible combobox with type-to-filter.
 * options: [{ value, label }]; value is compared as a string.
 */
const SearchableSelect = ({
  id,
  options = [],
  value,
  onChange,
  onBlur,
  placeholder = "Select",
  disabled = false,
  invalid = false,
  describedBy,
  searchPlaceholder = "Search...",
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const listId = `${id}-listbox`;

  const selected = options.find((o) => String(o.value) === String(value));
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => String(o.label).toLowerCase().includes(q)) : options;
  }, [options, query]);

  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) close(true);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  useEffect(() => {
    setActive(0);
  }, [query]);

  const close = (blur) => {
    setOpen(false);
    setQuery("");
    if (blur && onBlur) onBlur();
  };

  const choose = (option) => {
    onChange(option.value, option);
    close(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[active]) choose(filtered[active]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      close(true);
    }
  };

  return (
    <div className="fk-select" ref={rootRef}>
      <button
        type="button"
        id={id}
        className={`fk-input fk-select__trigger ${selected ? "" : "fk-select__trigger--placeholder"}`}
        disabled={disabled}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-invalid={invalid ? "true" : undefined}
        aria-describedby={describedBy}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (!open && (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        <span className="text-truncate">{selected ? selected.label : placeholder}</span>
        <FiChevronDown className="fk-select__chev" aria-hidden="true" />
      </button>
      {open && (
        <div className="fk-select__menu">
          <input
            ref={searchRef}
            type="text"
            className="fk-input fk-select__search"
            placeholder={searchPlaceholder}
            aria-label="Filter options"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          {filtered.length === 0 ? (
            <div className="fk-select__empty">No results</div>
          ) : (
            <ul className="fk-select__list" role="listbox" id={listId}>
              {filtered.map((o, i) => (
                <li
                  key={o.value}
                  role="option"
                  aria-selected={String(o.value) === String(value)}
                  className={`fk-select__opt ${i === active ? "fk-select__opt--active" : ""} ${
                    String(o.value) === String(value) ? "fk-select__opt--selected" : ""
                  }`}
                  onMouseEnter={() => setActive(i)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => choose(o)}
                >
                  {o.label}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
